import React, { Suspense } from "react";
import { Navigate } from "react-router-dom";
import { Routes, Route } from "react-router-dom";
import { useAuth } from "../config/AuthContext";
import DashboardLayout from "@/components/layout/DashboardLayout";
import Login from "@/pages/Login";
import useAuthUser from "@/hooks/useAuthUser";
import { getPostLoginPath } from "@/utils/agentUser";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

import ApiConnect from "@/pages/ApiConnect";
import LoginSuperAdmin from "@/pages/LoginSuperAdmin";
import LimcoLogin from "@/pages/limcoLogin";
import UserPermissionPage from "@/pages/Admin Panel/UserPermissionPage";
import CustomerPage from "@/pages/MaintainCustomer";
import ShipperInstructionLetter from "@/pages/superAdmin/pages/ShipperInstructionLetter";

// Shorthand helper — keeps imports readable without changing behavior
const lazy = (fn) => React.lazy(fn);

// ── Core / Dashboard ──────────────────────────────────────────────────────────
const Login2 = lazy(() => import("@/pages/Login2"));
const DashboardHome = lazy(() => import("@/pages/DashboardHome"));
const ModulesDashboard = lazy(() => import("@/pages/ModulesDashboard"));
const ForwarderDashboard = lazy(() => import("@/pages/ForwarderDashboard"));
const PurchaseOrder = lazy(() => import("@/pages/PurchaseOrder"));
const SalesOrder = lazy(() => import("@/pages/SalesOrder"));
const LoadPlan = lazy(() => import("@/pages/LoadPlan"));
const Shipment = lazy(() => import("@/pages/Shipment"));
const Mailbox = lazy(() => import("@/pages/Mailbox"));
const EmailSummaryDashboard = lazy(
    () => import("@/pages/EmailSummaryDashboard"),
);
const VisionBoard = lazy(() => import("@/pages/VisionBoard"));
const NotificationCenterPage = lazy(
    () => import("@/pages/NotificationCenterPage"),
);
const AddressBooking = lazy(() => import("@/pages/AddressBook"));

// ── WMS / Composer ─────────────────────────────────────────────────────────────
const WMS = lazy(() => import("@/pages/WMS"));
const Composer = lazy(() => import("@/pages/Composer"));

// ── Ocean Export ──────────────────────────────────────────────────────────────
const RateMatrix = lazy(() => import("@/pages/RateMatrix"));
const OceanControlTower = lazy(() => import("@/pages/OceanControlTower"));
const OceanSchedule = lazy(() => import("@/pages/OceanSchedule"));
const OceanContainerTracking = lazy(
    () => import("@/pages/Ocean/OceanContainerTracking"),
);
const RateIntelligence = lazy(() => import("@/pages/RateIntelligenceTabs"));
const RatesAgent = lazy(() => import("@/pages/RatesAgentTabs"));
const QuotesBookings = lazy(() => import("@/pages/QuotesBookings"));
const CustomsFiling = lazy(() => import("@/pages/CustomsFiling"));
const ContainerTrackingPage = lazy(
    () => import("@/pages/ContainerTrackingPage"),
);
const Financials = lazy(() => import("@/pages/Financials"));

// ── Rate Quotes ───────────────────────────────────────────────────────────────
const RateQuote = lazy(() => import("@/pages/RateQuote"));
const NewRateQuote = lazy(() => import("@/pages/RateQuote/NewRateQuote"));
const RateQuotePrint = lazy(() => import("@/pages/RateQuote/RateQuotePrint"));
const CancelRateQuote = lazy(() => import("@/pages/CancelRateQuote"));

// ── Bookings (Export) ─────────────────────────────────────────────────────────
const Booking = lazy(() => import("@/pages/Booking"));
const ExportBookingFormPage = lazy(
    () => import("@/pages/ExportBookingFormPage"),
);
const BookingPrint = lazy(() => import("@/components/booking/BookingPrint"));
const BookingPrintNonFreighted = lazy(
    () => import("@/components/booking/BookingPrintNonFreighted"),
);
const BookingPrintPdfDemo = lazy(
    () => import("@/components/PrintWrapper/BookingPrintPdfDemo"),
);
const CancelShipmentList = lazy(
    () => import("@/pages/Shipment/CancelShipmentList"),
);
const CancelShipment = lazy(() => import("@/pages/Shipment/CancelShipment"));

// ── Booking Request ───────────────────────────────────────────────────────────
const BookingRequestList = lazy(
    () => import("@/pages/BookingRequest/BookingRequestList"),
);
const NewBookingRequest = lazy(
    () => import("@/pages/BookingRequest/NewBookingRequest"),
);
const BookingRequestPrint = lazy(
    () => import("@/pages/BookingRequest/BookingRequestPrint"),
);

// ── HBL / MBL / Documents ────────────────────────────────────────────────────
const HBL = lazy(() => import("@/pages/HBL"));
const MBL = lazy(() => import("@/pages/MBL"));
const HBLPrint = lazy(() => import("@/components/HBL/HBLPrint"));
const CertificateOfOriginPrint = lazy(
    () => import("@/components/HBL/CertificateOfOriginPrint"),
);
const CustomerInvoicePrint = lazy(
    () => import("@/components/HBL/CustomerInvoicePrint"),
);
const PreAlertPrint = lazy(() => import("@/components/HBL/PreAlertPrint"));
const WorkOrderPrint = lazy(() => import("@/components/HBL/WorkOrderPrint"));
const PackingListPrint = lazy(
    () => import("@/components/HBL/PackingListPrint"),
);
const FCRPrint = lazy(() => import("@/components/HBL/FCRPrint"));
const CustomsInstructionsPrint = lazy(
    () => import("@/components/HBL/CustomsInstructionsPrint"),
);
const TransportOrderPrint = lazy(
    () => import("@/components/HBL/TransportOrderPrint"),
);
const CoverPagePrint = lazy(() => import("@/components/HBL/CoverPagePrint"));
const SecurityEndorsementPrint = lazy(
    () => import("@/components/booking/SecurityEndorsementPrint"),
);
const ScreeningConsentPrint = lazy(
    () => import("@/components/booking/ScreeningConsentPrint"),
);
const MBLPrint = lazy(() => import("@/components/MBL/MBLPrint"));
const StatusAudit = lazy(() => import("@/pages/StatusAudit"));
const ShipmentEventsList = lazy(() => import("@/pages/ShipmentEventsList"));
const PrintAuditStatusPgae = lazy(() => import("@/pages/PrintAuditStatusPgae"));
const DockReceipt = lazy(() => import("@/pages/DockReceipt/DockReceipt"));
const DockReceiptPrint = lazy(
    () => import("@/pages/DockReceipt/DockReceiptPrint"),
);

// ── Maintenance ───────────────────────────────────────────────────────────────
const MaintainVessels = lazy(() => import("@/pages/Vessel/MaintainVessel"));
const VesselModal = lazy(() => import("@/pages/Vessel/VesselModal"));
const Commodity = lazy(() => import("@/pages/Commodity"));
const ComposeMail = lazy(() => import("@/pages/Mail/ComposeMail"));
const CountryClause = lazy(() => import("@/pages/CountryClause"));
const MaintainClause = lazy(() => import("@/pages/Clause"));
const MaintainUsers = lazy(() => import("@/pages/Users"));
const ChargesCode = lazy(() => import("@/pages/ChargesCode"));
const PortPlace = lazy(() => import("@/pages/PortPlace/PortPlace"));
const MaintainLocations = lazy(
    () => import("@/pages/MaintainLocations/MaintainLocations"),
);
const ApplicableCharges = lazy(() => import("@/pages/ApplicableCharges"));
const OfficeTypes = lazy(() => import("@/pages/OfficeTypes/OfficeTypes"));
const TerminalMapping = lazy(
    () => import("@/pages/TerminalMapping/TerminalMapping"),
);
const TerminalMappingForm = lazy(
    () => import("@/pages/TerminalMapping/TerminalMappingForm"),
);

// ── AI Agents ─────────────────────────────────────────────────────────────────
const RFQ = lazy(() => import("@/pages/RFQ"));
const AIAgentSetup = lazy(() => import("@/pages/AIAgentSetup"));
const AIAgentDashboard = lazy(() => import("@/pages/AIAgentDashboard"));
const AgentControlTower = lazy(() => import("@/pages/AgentControlTower"));

const ComplianceAgent = lazy(() => import("@/pages/ComplianceAgent"));
const LoginHistory = lazy(() => import("@/pages/LoginHistory"));

const AIAgentReports = lazy(() => import("@/pages/AIAgentReports"));

// ── Reports (Export) ──────────────────────────────────────────────────────────
const QuotationReport = lazy(() => import("@/pages/QuotationReport"));
const BookingReport = lazy(() => import("@/pages/BookingReport"));
const AccountingReport = lazy(() => import("@/pages/AccountingReport"));
const ImportQuotationReport = lazy(
    () => import("@/pages/ImportQuotationReport"),
);
const ImportBookingReport = lazy(() => import("@/pages/ImportBookingReport"));
const ModuleQuotationReport = lazy(
    () => import("@/pages/ModuleQuotationReport"),
);
const ModuleBookingReport = lazy(() => import("@/pages/ModuleBookingReport"));

// ── Job Costing / Accounting ──────────────────────────────────────────────────
const JobCosting = lazy(() => import("@/pages/JobCosting"));
const ChartOfAccounts = lazy(
    () => import("@/pages/Accounting/ChartOfAccounts"),
);
const AccountingChargeCodes = lazy(
    () => import("@/pages/Accounting/ChargeCodes"),
);
const VendorBills = lazy(() => import("@/pages/Accounting/VendorBills"));
const JournalEntries = lazy(() => import("@/pages/Accounting/JournalEntries"));
const Payments = lazy(() => import("@/pages/Accounting/Payments"));
const AccountingReports = lazy(() => import("@/pages/Accounting/Reports"));
const CustomerInvoices = lazy(
    () => import("@/pages/Accounting/Invoice/CustomerInvoices"),
);

// ── Public Pages ──────────────────────────────────────────────────────────────
const QuoteResponse = lazy(() => import("@/pages/QuoteResponse"));
const SignupWizard = lazy(() => import("@/pages/SignupWizard"));
const SignupSuccess = lazy(() => import("@/pages/SignupSuccess"));

// ── Ocean Import Module ───────────────────────────────────────────────────────
const ImportRateQuoteList = lazy(
    () => import("@/pages/OceanImport/ImportRateQuoteList"),
);
const ImportRateQuoteForm = lazy(
    () => import("@/pages/OceanImport/ImportRateQuoteForm"),
);
const ImportRateQuotePrint = lazy(
    () => import("@/pages/OceanImport/RateQuote/ImportRateQuotePrint"),
);
const ImportBookingList = lazy(
    () => import("@/pages/OceanImport/ImportBooking/ImportBookingList"),
);
const CancelImportShipment = lazy(
    () => import("@/pages/OceanImport/ImportBooking/CancelImportShipment"),
);
const ImportCancelBookingRequest = lazy(
    () => import("@/pages/OceanImport/ImportCancelBookingRequest"),
);

const ImportBookingForm = lazy(
    () => import("@/pages/OceanImport/ImportBooking/ImportBookingForm"),
);
const ImportStatusAudit = lazy(
    () => import("@/pages/OceanImport/ImportBooking/ImportStatusAudit"),
);
const ImportPrintAuditStatusPgae = lazy(
    () =>
        import("@/pages/OceanImport/ImportBooking/PrintAudit/PrintAuditStatusPgae"),
);
const ReleaseOrderList = lazy(
    () => import("@/pages/OceanImport/ImportBooking/ReleaseOrderList"),
);
const ReleaseOrderForm = lazy(
    () => import("@/pages/OceanImport/ImportBooking/ReleaseOrderForm"),
);
const ReleaseOrderPrint = lazy(
    () => import("@/pages/OceanImport/ImportBooking/ReleaseOrderPrint"),
);
const DeliveryOrderForm = lazy(
    () => import("@/pages/OceanImport/ImportBooking/DeliveryOrderForm"),
);
const DeliveryOrderPrint = lazy(
    () => import("@/pages/OceanImport/ImportBooking/DeliveryOrderPrint"),
);
const BookingImportPrint = lazy(
    () => import("@/pages/OceanImport/ImportBooking/BookingImportPrint"),
);
const BookingImportPrintNonFreight = lazy(
    () => import("@/components/ImportPrint/BookingImportPrintNonFreight"),
);
const ImportHBLPrint = lazy(
    () => import("@/components/ImportPrint/importHBLPrint"),
);
const ImportPreAlertConsigneePrint = lazy(
    () => import("@/components/ImportPrint/ImportPreAlertConsigneePrint"),
);
const ImportBookingConfirmationPrint = lazy(
    () => import("@/components/ImportPrint/ImportBookingConfirmationPrint"),
);
const ISFPrint = lazy(() => import("@/components/ImportPrint/ISFPrint"));
const LockerFreightImportBookingPrint = lazy(
    () => import("@/components/ImportPrint/LockerFreightImportBookingPrint"),
);

// ── FCL Ocean Reports ─────────────────────────────────────────────────────────
const FclReportsHome = lazy(
    () => import("@/pages/reports/ocean/reports/FclReportsHome"),
);
const FclExportBookingReportPage = lazy(
    () =>
        import("@/pages/reports/ocean/reports/export/FclExportBookingReportPage"),
);
const FclExportBookingSalespersonReportPage = lazy(
    () =>
        import("@/pages/reports/ocean/reports/export/FclExportBookingSalespersonReportPage"),
);

const FclExportDestinationCountryWiseTEUSReport = lazy(
    () =>
        import("@/pages/reports/ocean/reports/export/FclExportDestinationCountryWiseTEUSReport"),
);

const FclExportDocCoAlertReport = lazy(
    () =>
        import("@/pages/reports/ocean/reports/export/FclExportDocCoAlertReport"),
);

const FclExportTopFiftyCustomerReport = lazy(
    () =>
        import("@/pages/reports/ocean/reports/export/FclExportTopFiftyCustomerReport"),
);

const FclExportAutoCutOffReport = lazy(
    () =>
        import("@/pages/reports/ocean/reports/export/FclExportAutoCutOffReportPage"),
);

const FclExportMBLProofingReport = lazy(
    () =>
        import("@/pages/reports/ocean/reports/export/FclExportMBLProofingReport"),
);

const FclExportTaskAuditReport = lazy(
    () => import("@/pages/reports/ocean/reports/export/FclExportTaskAuditReport"),
);
const FclExportDailySalesPersonReport = lazy(
    () =>
        import("@/pages/reports/ocean/reports/export/FclExportSalesPersonReport"),
);

const FclExportCustomerLossReport = lazy(
    () =>
        import("@/pages/reports/ocean/reports/export/FclExportCustomerLossReport"),
);

const FclExportTUEsReport = lazy(
    () => import("@/pages/reports/ocean/reports/export/FclExportTUEsReport"),
);

const FclExportActivaReport = lazy(
    () => import("@/pages/reports/ocean/reports/export/FclExportActivaReport"),
);

const FclExportOnboardReport = lazy(
    () => import("@/pages/reports/ocean/reports/export/FclExportOnboardReport"),
);

const FclExportDailyReport = lazy(
    () => import("@/pages/reports/ocean/reports/export/FclExportDailyReport"),
);

const FclExportDailyReportOldReference = lazy(
    () =>
        import(
            "@/pages/reports/ocean/reports/export/FclExportDailyReportOldReference"
        ),
);

const FclExportVesselReport = lazy(
    () => import("@/pages/reports/ocean/reports/export/FclExportVesselReport"),
);

const FclExportProfitAndLossReport = lazy(
    () =>
        import("@/pages/reports/ocean/reports/export/FclExportProfitAndLossReport"),
);

const FclExportRateQuoteReport = lazy(
    () =>
        import("@/pages/reports/ocean/reports/export/FclExportRateQuoteReportPage"),
);

const FclExportOnboardStatusReport = lazy(
    () =>
        import("@/pages/reports/ocean/reports/export/FclExportOnboardStatusReport"),
);

const FclExportAdvancedStatusReport = lazy(
    () =>
        import("@/pages/reports/ocean/reports/export/FclExportAdvancedStatusReport"),
);

const FclImportBookingReportPage = lazy(
    () =>
        import("@/pages/reports/ocean/reports/import/FclImportBookingReportPage"),
);
const FclImportActiveShipmentReportPage = lazy(
    () =>
        import("@/pages/reports/ocean/reports/import/FclImportActiveShipmentReportPage"),
);
const FclImportContainerDemurrageReportPage = lazy(
    () =>
        import("@/pages/reports/ocean/reports/import/FclImportContainerDemurrageReportPage"),
);
const FclImportTruckingReportPage = lazy(
    () =>
        import("@/pages/reports/ocean/reports/import/FclImportTruckingReportPage"),
);
const FclImportStatusAuditReportPage = lazy(
    () =>
        import("@/pages/reports/ocean/reports/import/FclImportStatusAuditReportPage"),
);
const FclImportContainerTrackingReportPage = lazy(
    () =>
        import("@/pages/reports/ocean/reports/import/FclImportContainerTrackingReportPage"),
);
const FclImportArrivalNoticeReportPage = lazy(
    () =>
        import("@/pages/reports/ocean/reports/import/FclImportArrivalNoticeReportPage"),
);
const FclImportEtaStatusReportPage = lazy(
    () =>
        import("@/pages/reports/ocean/reports/import/FclImportEtaStatusReportPage"),
);
const FclImportFileCancellationReportPage = lazy(
    () =>
        import("@/pages/reports/ocean/reports/import/FclImportFileCancellationReportPage"),
);

const FclImportProfitAndLossBasedOnBooking = lazy(
    () =>
        import("@/pages/reports/ocean/reports/import/FclImportProfitAndLossBasedOnBooking"),
);
const FclImportGrossProfitLossReport = lazy(
    () =>
        import("@/pages/reports/ocean/reports/import/FclImportGrossProfitLossReport"),
);

const FclImportArrivalNoticePreAlertReportPage = lazy(
    () =>
        import("@/pages/reports/ocean/reports/import/FclImportArrivalNoticePreAlertReportPage"),
);
const FclImportProfitLossReportPage = lazy(
    () =>
        import("@/pages/reports/ocean/reports/import/FclImportProfitLossReportPage"),
);
const FclImportRateQuoteReportPage = lazy(
    () =>
        import("@/pages/reports/ocean/reports/import/FclImportRateQuoteReportPage"),
);
const OceanReportPage = lazy(
    () => import("@/pages/Ocean/reports/OceanReportPage"),
);

// ── LCL Ocean Module ─────────────────────────────────────────────────────────

const LclRateQuote = lazy(
    () => import("@/pages/lcl/export/RateQuote")
)

const LclRateQuoteForm = lazy(
    () => import("@/pages/lcl/export/LclRateQuoteForm")
)

// ── Air Freight ───────────────────────────────────────────────────────────────
const AirDashboard = lazy(() => import("@/pages/AirFreight/AirDashboard"));
const AirShipmentDetail = lazy(
    () => import("@/pages/AirFreight/AirShipmentDetail"),
);
const AirTracking = lazy(() => import("@/pages/AirFreight/AirTracking"));
const AirRateQuoteList = lazy(
    () => import("@/pages/AirFreight/AirRateQuoteList"),
);
const AirRateQuoteForm = lazy(
    () => import("@/pages/AirFreight/AirRateQuoteForm"),
);
const AirRateQuotePrint = lazy(
    () => import("@/pages/AirFreight/AirRateQuotePrint"),
);
const AirCancelRateQuoteList = lazy(
    () => import("@/pages/AirFreight/AirCancelRateQuoteList"),
);
const AirStatusAudit = lazy(() => import("@/pages/AirFreight/AirStatusAudit"));
const BookingPage = lazy(
    () => import("@/pages/AirFreight/booking/BookingPage"),
);
// Named exports from shared modules — use .then() to extract the specific export
const AirExportList = lazy(() =>
    import("@/pages/AirFreight/AirShipmentList").then((m) => ({
        default: m.AirExportList,
    })),
);
const CargoAiOnlineBookings = lazy(
    () => import("@/pages/AirFreight/CargoAiOnlineBookings"),
);
const AirImportList = lazy(() =>
    import("@/pages/AirFreight/AirShipmentList").then((m) => ({
        default: m.AirImportList,
    })),
);
const HawbPage = lazy(() => import("@/pages/AirFreight/booking/HawbPage"));
const MawbPage = lazy(() => import("@/pages/AirFreight/booking/MawbPage"));
const AirImportPreAlert = lazy(() =>
    import("@/pages/AirFreight/Phase2Pages").then((m) => ({
        default: m.AirImportPreAlert,
    })),
);
const AirImportArrivalNotice = lazy(() =>
    import("@/pages/AirFreight/Phase2Pages").then((m) => ({
        default: m.AirImportArrivalNotice,
    })),
);
const AirImportCustoms = lazy(() =>
    import("@/pages/AirFreight/Phase2Pages").then((m) => ({
        default: m.AirImportCustoms,
    })),
);
const AirImportDeliveryOrder = lazy(() =>
    import("@/pages/AirFreight/Phase2Pages").then((m) => ({
        default: m.AirImportDeliveryOrder,
    })),
);
const AirImportCargoDelivery = lazy(() =>
    import("@/pages/AirFreight/Phase2Pages").then((m) => ({
        default: m.AirImportCargoDelivery,
    })),
);
const AirImportPOD = lazy(() =>
    import("@/pages/AirFreight/Phase2Pages").then((m) => ({
        default: m.AirImportPOD,
    })),
);
const ExportCancelShipmentList = lazy(() =>
    import("@/pages/AirFreight/AuxLists").then((m) => ({
        default: m.ExportCancelShipmentList,
    })),
);
const ImportCancelShipmentList = lazy(() =>
    import("@/pages/AirFreight/AuxLists").then((m) => ({
        default: m.ImportCancelShipmentList,
    })),
);

// ── Domestic Freight ──────────────────────────────────────────────────────────
const DomesticRateQuoteList = lazy(() =>
    import("@/pages/Domestic").then((m) => ({
        default: m.DomesticRateQuoteList,
    })),
);
const DomesticRateQuoteForm = lazy(() =>
    import("@/pages/Domestic").then((m) => ({
        default: m.DomesticRateQuoteForm,
    })),
);
const DomesticWorkOrderList = lazy(() =>
    import("@/pages/Domestic").then((m) => ({
        default: m.DomesticWorkOrderList,
    })),
);
const DomesticWorkOrderForm = lazy(() =>
    import("@/pages/Domestic").then((m) => ({
        default: m.DomesticWorkOrderForm,
    })),
);
const DomesticReportsHome = lazy(
    () => import("@/pages/Domestic/reports/DomesticReportsHome"),
);
const DomesticReportPage = lazy(
    () => import("@/pages/Domestic/reports/DomesticReportPage"),
);
const AirReportsHome = lazy(
    () => import("@/pages/AirFreight/reports/AirReportsHome"),
);
const AirReportPage = lazy(
    () => import("@/pages/AirFreight/reports/AirReportPage"),
);
const AirExportTopFiftyCustomerReport = lazy(
    () => import("@/pages/AirFreight/reports/export/AirExportTopFiftyCustomerReport"),
);
const AirExportRateQuoteReport = lazy(
    () => import("@/pages/AirFreight/reports/export/AirExportRateQuoteReport"),
);

const ImportCancelRateQuoteList = lazy(
    () => import("@/pages/OceanImport/ImportCancelRateQuoteList"),
);

// ── Admin ─────────────────────────────────────────────────────────────────────
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));
const AdminLogin = lazy(() => import("@/pages/superAdmin/Login"));
const AdminRoutes = lazy(() => import("./AdminRoutes"));

const LOGIN2_BG_IMAGE = `${process.env.PUBLIC_URL || ""}/loginpage-bg.webp`;

const Login2SuspenseFallback = () => (
    <div
        className="min-h-screen bg-black bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${LOGIN2_BG_IMAGE})` }}
        aria-hidden="true"
    />
);

const MainRoutes = () => {
    const { isAuthenticated } = useAuth();
    const authUser = useAuthUser();

    const isSuperAdmin = authUser?.roleID === 1;

    const hasCompany = authUser?.companyId != null;

    const isNormalUser = isAuthenticated && !isSuperAdmin && hasCompany;

    const isSignupPending = isAuthenticated && !isSuperAdmin && !hasCompany;

    if (!isAuthenticated) {
        return (
            <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                    <Route path="/quote-response" element={<QuoteResponse />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/" element={<Navigate to="/infox/login" replace />} />
                    <Route path="/locker-freight/login" element={<Login2 />} />
                    <Route path="/infox/login" element={<Login2 />} />
                    <Route path="/limco/login" element={<LimcoLogin />} />
                    <Route path="/signin" element={<LoginSuperAdmin />} />
                    <Route path="/signup" element={<SignupWizard />} />
                    <Route path="/signup/success" element={<SignupSuccess />} />
                    <Route path="*" element={<Navigate to="/infox/login" replace />} />
                </Routes>
            </Suspense>
        );
    }

    if (isSuperAdmin) {
        return (
            <Routes>
                <Route
                    path="admin/*"
                    element={
                        <DashboardLayout>
                            <AdminRoutes />
                        </DashboardLayout>
                    }
                />

                <Route path="*" element={<Navigate to="/admin/" replace />} />
            </Routes>
        );
    }

    if (isSignupPending) {
        return (
            <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                    <Route path="/quote-response" element={<QuoteResponse />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signin" element={<Login />} />
                    <Route path="/signup" element={<SignupWizard />} />
                    <Route path="/signup/success" element={<SignupSuccess />} />
                    <Route path="/*" element={<Navigate to="/login" replace />} />
                </Routes>
            </Suspense>
        );
    }

    return (
        <Routes>
            {/* {isAuthenticated && authUser.roleID === 2 ? ( */}
            {isNormalUser && (
                <>
                    <Route
                        path="/modules"
                        element={
                            <DashboardLayout>
                                <ModulesDashboard />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/dashboard"
                        element={
                            <DashboardLayout>
                                <DashboardHome />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/forwarder-dashboard"
                        element={
                            <DashboardLayout>
                                <ForwarderDashboard />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/operations-dashboard"
                        element={
                            <DashboardLayout>
                                <ForwarderDashboard />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/purchase-orders"
                        element={
                            <DashboardLayout>
                                <PurchaseOrder />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/sales-orders"
                        element={
                            <DashboardLayout>
                                <SalesOrder />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/load-plan"
                        element={
                            <DashboardLayout>
                                <LoadPlan />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ShipmentList"
                        element={
                            <DashboardLayout>
                                <Shipment />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/mailbox"
                        element={
                            <DashboardLayout>
                                <Mailbox />
                            </DashboardLayout>
                        }
                    />
                    {/* WMS / Warehousing */}
                    <Route
                        path="/wms"
                        element={
                            <DashboardLayout>
                                <WMS />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/wms/:tab"
                        element={
                            <DashboardLayout>
                                <WMS />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/wms/reports/:reportId"
                        element={
                            <DashboardLayout>
                                <WMS />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/warehousing"
                        element={
                            <DashboardLayout>
                                <WMS />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/warehousing/:tab"
                        element={
                            <DashboardLayout>
                                <WMS />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/warehousing/reports/:reportId"
                        element={
                            <DashboardLayout>
                                <WMS />
                            </DashboardLayout>
                        }
                    />
                    {/* AI Agent Composer */}
                    <Route
                        path="/composer"
                        element={
                            <DashboardLayout>
                                <Composer />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/composer/:tab"
                        element={
                            <DashboardLayout>
                                <Composer />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/composer/:tab/:id"
                        element={
                            <DashboardLayout>
                                <Composer />
                            </DashboardLayout>
                        }
                    />
                    {/* VisionBoard */}
                    <Route
                        path="/vision-board"
                        element={
                            <DashboardLayout>
                                <VisionBoard />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/api-connect"
                        element={
                            <DashboardLayout>
                                <ApiConnect />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/email-summary"
                        element={
                            <DashboardLayout>
                                <EmailSummaryDashboard />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ratematrix"
                        element={
                            <DashboardLayout>
                                <RateMatrix />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean-control-tower"
                        element={
                            <DashboardLayout>
                                <OceanControlTower />
                            </DashboardLayout>
                        }
                    />
                    {/* Booking request */}
                    <Route
                        path="/booking-request-list"
                        element={
                            <DashboardLayout>
                                <BookingRequestList />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/booking-request/new"
                        element={
                            <DashboardLayout>
                                <NewBookingRequest />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/booking-request/edit/:id"
                        element={
                            <DashboardLayout>
                                <NewBookingRequest />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/booking-request/print/:id"
                        element={<BookingRequestPrint />}
                    />
                    {/* Booking */}
                    <Route
                        path="/booking"
                        element={
                            <DashboardLayout>
                                <Shipment />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/booking/new"
                        element={
                            <DashboardLayout>
                                <ExportBookingFormPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/booking/edit/:id"
                        element={
                            <DashboardLayout>
                                <ExportBookingFormPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/booking/shipper-instruction-letter/:id"
                        element={
                            <DashboardLayout>
                                <ShipperInstructionLetter />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/booking/cancel"
                        element={
                            <DashboardLayout>
                                <CancelShipment />
                            </DashboardLayout>
                        }
                    />
                    <Route path="/booking/print/:id" element={<BookingPrint />} />
                    <Route
                        path="/booking/print-non-freighted/:id"
                        element={<BookingPrintNonFreighted />}
                    />
                    {/* Demo: same booking API, but the Download button produces a
                        real vector PDF via @react-pdf/renderer (ProfessionalPdf). */}
                    <Route
                        path="/booking/pdf-demo/:id"
                        element={<BookingPrintPdfDemo />}
                    />
                    {/* Address Book */}
                    <Route
                        path="/address-book"
                        element={
                            <DashboardLayout>
                                <AddressBooking />
                            </DashboardLayout>
                        }
                    />
                    {/* AI Insights submenu routes */}
                    <Route
                        path="/rate-agent"
                        element={
                            <DashboardLayout>
                                <RateIntelligence />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ratesagent"
                        element={
                            <DashboardLayout>
                                <RatesAgent />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/quotes-bookings"
                        element={
                            <DashboardLayout>
                                <QuotesBookings />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/customs"
                        element={
                            <DashboardLayout>
                                <CustomsFiling />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/containers"
                        element={
                            <DashboardLayout>
                                <ContainerTrackingPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/financials"
                        element={
                            <DashboardLayout>
                                <Financials />
                            </DashboardLayout>
                        }
                    />
                    {/* Reports submenu routes */}
                    <Route
                        path="/quotation-report"
                        element={
                            <DashboardLayout>
                                <QuotationReport />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/booking-report"
                        element={
                            <DashboardLayout>
                                <BookingReport />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/accounting-report"
                        element={
                            <DashboardLayout>
                                <AccountingReport />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-quotation-report"
                        element={
                            <DashboardLayout>
                                <ImportQuotationReport />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-booking-report"
                        element={
                            <DashboardLayout>
                                <ImportBookingReport />
                            </DashboardLayout>
                        }
                    />
                    {/* Module-specific Quotation & Booking reports (Air & Domestic) */}
                    <Route
                        path="/air/export/quotation-report"
                        element={
                            <DashboardLayout>
                                <AirExportRateQuoteReport />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/quotation-report"
                        element={
                            <DashboardLayout>
                                <ModuleQuotationReport moduleKey="air-import" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/export/booking-report"
                        element={
                            <DashboardLayout>
                                <ModuleBookingReport moduleKey="air-export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/booking-report"
                        element={
                            <DashboardLayout>
                                <ModuleBookingReport moduleKey="air-import" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/export/quotation-report"
                        element={
                            <DashboardLayout>
                                <ModuleQuotationReport moduleKey="domestic-export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/import/quotation-report"
                        element={
                            <DashboardLayout>
                                <ModuleQuotationReport moduleKey="domestic-import" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/export/booking-report"
                        element={
                            <DashboardLayout>
                                <ModuleBookingReport moduleKey="domestic-export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/import/booking-report"
                        element={
                            <DashboardLayout>
                                <ModuleBookingReport moduleKey="domestic-import" />
                            </DashboardLayout>
                        }
                    />
                    {/* FCL Report Manager — one route per report (see ocean/reports/import|export/) */}
                    <Route
                        path="/ocean/export/reports"
                        element={
                            <DashboardLayout>
                                <FclReportsHome kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/export/reports/booking"
                        element={
                            <DashboardLayout>
                                <FclExportBookingReportPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/export/reports/booking-salesperson"
                        element={
                            <DashboardLayout>
                                <FclExportBookingSalespersonReportPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/export/reports/destination-teu"
                        element={
                            <DashboardLayout>
                                <FclExportDestinationCountryWiseTEUSReport />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/export/reports/doc-co-alert"
                        element={
                            <DashboardLayout>
                                <FclExportDocCoAlertReport />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/export/reports/top-50-customer"
                        element={
                            <DashboardLayout>
                                <FclExportTopFiftyCustomerReport />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/export/reports/auto-cutoff"
                        element={
                            <DashboardLayout>
                                <FclExportAutoCutOffReport />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/export/reports/mbl-proofing"
                        element={
                            <DashboardLayout>
                                <FclExportMBLProofingReport />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/export/reports/task-audit-report"
                        element={
                            <DashboardLayout>
                                <FclExportTaskAuditReport />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/export/reports/sales-person-report"
                        element={
                            <DashboardLayout>
                                <FclExportDailySalesPersonReport />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/export/reports/customer-loss"
                        element={
                            <DashboardLayout>
                                <FclExportCustomerLossReport />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/export/reports/tues-report"
                        element={
                            <DashboardLayout>
                                <FclExportTUEsReport />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/export/reports/activa-report"
                        element={
                            <DashboardLayout>
                                <FclExportActivaReport />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/export/reports/onboard-report"
                        element={
                            <DashboardLayout>
                                <FclExportOnboardReport />
                            </DashboardLayout>
                        }
                    />

                    <Route
                        path="/ocean/export/reports/daily-report"
                        element={
                            <DashboardLayout>
                                <FclExportDailyReport />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/export/reports/daily-report"
                        element={
                            <DashboardLayout>
                                <FclExportDailyReport />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/export/reports/daily-report-old-reference-number"
                        element={
                            <DashboardLayout>
                                <FclExportDailyReportOldReference />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/export/reports/italy-vessel-report"
                        element={
                            <DashboardLayout>
                                <FclExportVesselReport />
                            </DashboardLayout>
                        }
                    />

                    <Route
                        path="/ocean/export/reports/profit-and-loss-report"
                        element={
                            <DashboardLayout>
                                <FclExportProfitAndLossReport />
                            </DashboardLayout>
                        }
                    />

                    <Route
                        path="/ocean/export/reports/rate-quote"
                        element={
                            <DashboardLayout>
                                <FclExportRateQuoteReport />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/export/reports/onboard-status"
                        element={
                            <DashboardLayout>
                                <FclExportOnboardStatusReport />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/export/reports/advanced-status"
                        element={
                            <DashboardLayout>
                                <FclExportAdvancedStatusReport />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports"
                        element={
                            <DashboardLayout>
                                <FclReportsHome kind="import" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/booking"
                        element={
                            <DashboardLayout>
                                <FclImportBookingReportPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/active-shipment"
                        element={
                            <DashboardLayout>
                                <FclImportActiveShipmentReportPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/demurrage"
                        element={
                            <DashboardLayout>
                                <FclImportContainerDemurrageReportPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/trucking"
                        element={
                            <DashboardLayout>
                                <FclImportTruckingReportPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/status-audit"
                        element={
                            <DashboardLayout>
                                <FclImportStatusAuditReportPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/container-tracking"
                        element={
                            <DashboardLayout>
                                <FclImportContainerTrackingReportPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/arrival-notice"
                        element={
                            <DashboardLayout>
                                <FclImportArrivalNoticeReportPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/eta-status"
                        element={
                            <DashboardLayout>
                                <FclImportEtaStatusReportPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/file-cancellation-report"
                        element={
                            <DashboardLayout>
                                <FclImportFileCancellationReportPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/profit-and-loss-based-on-booking"
                        element={
                            <DashboardLayout>
                                <FclImportProfitAndLossBasedOnBooking />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/gross-profit-loss-report"
                        element={
                            <DashboardLayout>
                                <FclImportGrossProfitLossReport />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/export/reports/file-cancellation"
                        element={
                            <DashboardLayout>
                                <FclImportFileCancellationReportPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/arrival-notice-pre-alert-report"
                        element={
                            <DashboardLayout>
                                <FclImportArrivalNoticePreAlertReportPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/profit-loss-report"
                        element={
                            <DashboardLayout>
                                <FclImportProfitLossReportPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/rate-quote-report"
                        element={
                            <DashboardLayout>
                                <FclImportRateQuoteReportPage />
                            </DashboardLayout>
                        }
                    />
                    {/* OCEAN submenu routes */}
                    <Route
                        path="/rate-quote"
                        element={
                            <DashboardLayout>
                                <RateQuote />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/cancel-rate-quote"
                        element={
                            <DashboardLayout>
                                <CancelRateQuote />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/rate-quote/new"
                        element={
                            <DashboardLayout>
                                <NewRateQuote />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/rate-quote/edit/:id"
                        element={
                            <DashboardLayout>
                                <NewRateQuote />
                            </DashboardLayout>
                        }
                    />
                    <Route path="/rate-quote/print/:id" element={<RateQuotePrint />} />
                    {/* HBL & MBL routes */}
                    <Route
                        path="/hbl"
                        element={
                            <DashboardLayout>
                                <HBL />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/hblPrint"
                        element={
                            <DashboardLayout>
                                <HBLPrint />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/certificateOfOriginPrint"
                        element={
                            <DashboardLayout>
                                <CertificateOfOriginPrint />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/customerInvoicePrint"
                        element={
                            <DashboardLayout>
                                <CustomerInvoicePrint />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/preAlertPrint"
                        element={
                            <DashboardLayout>
                                <PreAlertPrint />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/workOrderPrint"
                        element={
                            <DashboardLayout>
                                <WorkOrderPrint />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/packingListPrint"
                        element={
                            <DashboardLayout>
                                <PackingListPrint />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/fcrPrint"
                        element={
                            <DashboardLayout>
                                <FCRPrint />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/customsInstructionsPrint"
                        element={
                            <DashboardLayout>
                                <CustomsInstructionsPrint />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/transportOrderPrint"
                        element={
                            <DashboardLayout>
                                <TransportOrderPrint />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/coverPagePrint"
                        element={
                            <DashboardLayout>
                                <CoverPagePrint />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/securityEndorsementPrint"
                        element={<SecurityEndorsementPrint />}
                    />
                    <Route
                        path="/screeningConsentPrint"
                        element={<ScreeningConsentPrint />}
                    />
                    <Route
                        path="/mbl"
                        element={
                            <DashboardLayout>
                                <MBL />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/mblPrint"
                        element={
                            <DashboardLayout>
                                <MBLPrint />
                            </DashboardLayout>
                        }
                    />
                    {/* Status Audit route */}
                    <Route
                        path="/status-audit"
                        element={
                            <DashboardLayout>
                                <StatusAudit />
                            </DashboardLayout>
                        }
                    />
                    {/* Shipment EDI Events */}
                    <Route
                        path="/shipment-events"
                        element={
                            <DashboardLayout>
                                <ShipmentEventsList />
                            </DashboardLayout>
                        }
                    />
                    {/* RFQ route */}
                    <Route
                        path="/rfq"
                        element={
                            <DashboardLayout>
                                <RFQ />
                            </DashboardLayout>
                        }
                    />
                    {/* AI Agent routes */}
                    <Route
                        path="/ai-agent-setup"
                        element={
                            <DashboardLayout>
                                <AIAgentSetup />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ai-agent-dashboard"
                        element={
                            <DashboardLayout>
                                <AIAgentDashboard />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/agent-control-tower"
                        element={
                            <DashboardLayout>
                                <AgentControlTower />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ai-agent-reports"
                        element={
                            <DashboardLayout>
                                <AIAgentReports />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/compliance-agent"
                        element={
                            <DashboardLayout>
                                <ComplianceAgent />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/maintain-vessel"
                        element={
                            <DashboardLayout>
                                <MaintainVessels />
                            </DashboardLayout>
                        }
                    />
                    {/* Booking */}
                    <Route
                        path="/Commodity"
                        element={
                            <DashboardLayout>
                                <Commodity />
                            </DashboardLayout>
                        }
                    />
                    {/* Mail Compose */}
                    <Route
                        path="/mail/compose"
                        element={
                            <DashboardLayout>
                                <ComposeMail />
                            </DashboardLayout>
                        }
                    />
                    {/* System Maintenance */}
                    <Route
                        path="/CountryClause"
                        element={
                            <DashboardLayout>
                                <CountryClause />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/Clause"
                        element={
                            <DashboardLayout>
                                <MaintainClause />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ChargesCode"
                        element={
                            <DashboardLayout>
                                <ChargesCode />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/status-audit/print"
                        element={<PrintAuditStatusPgae />}
                    />
                    <Route
                        path="/status-audit/print/:id"
                        element={<PrintAuditStatusPgae />}
                    />
                    <Route
                        path="/port-place"
                        element={
                            <DashboardLayout>
                                <PortPlace />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/Users"
                        element={
                            <DashboardLayout>
                                <MaintainUsers />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/customers"
                        element={
                            <DashboardLayout>
                                <CustomerPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/login-history"
                        element={
                            <DashboardLayout>
                                <LoginHistory />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/applicable-charges"
                        element={
                            <DashboardLayout>
                                <ApplicableCharges />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/office-type"
                        element={
                            <DashboardLayout>
                                <OfficeTypes />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/terminal-mapping"
                        element={
                            <DashboardLayout>
                                <TerminalMapping />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/terminal-mapping/new"
                        element={
                            <DashboardLayout>
                                <TerminalMappingForm />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/terminal-mapping/edit/:id"
                        element={
                            <DashboardLayout>
                                <TerminalMappingForm />
                            </DashboardLayout>
                        }
                    />
                    {/* Notification Center */}
                    <Route
                        path="/notifications"
                        element={
                            <DashboardLayout>
                                <NotificationCenterPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/dock-receipt/:bookingId"
                        element={
                            <DashboardLayout>
                                <DockReceipt />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/dock-receipt/print/:bookingId"
                        element={<DockReceiptPrint />}
                    />
                    {/* Job Costing */}
                    <Route
                        path="/job-costing/:bookingId"
                        element={
                            <DashboardLayout>
                                <JobCosting />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-job-costing/:bookingId"
                        element={
                            <DashboardLayout>
                                <JobCosting />
                            </DashboardLayout>
                        }
                    />
                    {/* Accounting Module */}
                    <Route
                        path="/accounting"
                        element={
                            <DashboardLayout>
                                <ChartOfAccounts />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/accounting/chart-of-accounts"
                        element={
                            <DashboardLayout>
                                <ChartOfAccounts />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/accounting/charge-codes"
                        element={
                            <DashboardLayout>
                                <AccountingChargeCodes />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/accounting/invoices"
                        element={
                            <DashboardLayout>
                                <CustomerInvoices />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/accounting/bills"
                        element={
                            <DashboardLayout>
                                <VendorBills />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/accounting/journals"
                        element={
                            <DashboardLayout>
                                <JournalEntries />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/accounting/payments"
                        element={
                            <DashboardLayout>
                                <Payments />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/accounting/reports"
                        element={
                            <DashboardLayout>
                                <AccountingReports />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/maintain-locations"
                        element={
                            <DashboardLayout>
                                <MaintainLocations />
                            </DashboardLayout>
                        }
                    />
                    {/* Ocean Import Module */}
                    <Route
                        path="/import-rate-quote"
                        element={
                            <DashboardLayout>
                                <ImportRateQuoteList />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-cancel-rate-quote"
                        element={
                            <DashboardLayout>
                                <ImportCancelRateQuoteList />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-rate-quote/new"
                        element={
                            <DashboardLayout>
                                <ImportRateQuoteForm />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-rate-quote/edit/:id"
                        element={
                            <DashboardLayout>
                                <ImportRateQuoteForm />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-rate-quote/print/:id"
                        element={<ImportRateQuotePrint />}
                    />
                    <Route
                        path="/import-booking"
                        element={
                            <DashboardLayout>
                                <ImportBookingList />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-booking-fcl"
                        element={
                            <DashboardLayout>
                                <ImportBookingList shippingTerm="FCL" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-booking-lcl"
                        element={
                            <DashboardLayout>
                                <ImportBookingList shippingTerm="LCL" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-cancel-booking"
                        element={
                            <DashboardLayout>
                                <ImportCancelBookingRequest />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-booking/cancel"
                        element={
                            <DashboardLayout>
                                <CancelImportShipment />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-booking/new"
                        element={
                            <DashboardLayout>
                                <ImportBookingForm />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-booking/edit/:id"
                        element={
                            <DashboardLayout>
                                <ImportBookingForm />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-booking/Print/:id"
                        element={<BookingImportPrint />}
                    />
                    <Route
                        path="/import-booking/PrintLockerFreight/:id"
                        element={
                            <DashboardLayout>
                                <LockerFreightImportBookingPrint />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-booking/PrintNonFreight/:id"
                        element={<BookingImportPrintNonFreight />}
                    />
                    <Route
                        path="/release-order"
                        element={
                            <DashboardLayout>
                                <ReleaseOrderList />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/release-order/new"
                        element={
                            <DashboardLayout>
                                <ReleaseOrderForm />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/release-order/edit"
                        element={
                            <DashboardLayout>
                                <ReleaseOrderForm />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/release-order/print/:id"
                        element={<ReleaseOrderPrint />}
                    />
                    <Route
                        path="/delivery-order/new"
                        element={
                            <DashboardLayout>
                                <DeliveryOrderForm />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/delivery-order/edit/:id"
                        element={
                            <DashboardLayout>
                                <DeliveryOrderForm />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/delivery-order/print/:id"
                        element={<DeliveryOrderPrint />}
                    />
                    <Route
                        path="/import-booking/status-audit/:id"
                        element={
                            <DashboardLayout>
                                <ImportStatusAudit />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-status-audit/print/:companyId/:bookingId"
                        element={<ImportPrintAuditStatusPgae />}
                    />
                    <Route
                        path="/import-hbl-print/:bookingId"
                        element={
                            <DashboardLayout>
                                <ImportHBLPrint />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-pre-alert-consignee-print/:bookingId"
                        element={<ImportPreAlertConsigneePrint />}
                    />
                    <Route
                        path="/import-booking-confirmation-print/:bookingId"
                        element={<ImportBookingConfirmationPrint />}
                    />
                    <Route
                        path="/import-isf-print/:bookingId"
                        element={
                            <DashboardLayout>
                                <ISFPrint />
                            </DashboardLayout>
                        }
                    />
                    {/* ──────────────────────────── LCL MODULE ──────────────────────────── */}

                    <Route
                        path="/lcl/export/rate-quotes"
                        element={
                            <DashboardLayout>
                                <LclRateQuote />
                            </DashboardLayout>
                        }
                    />

                    <Route
                        path="/lcl/export/rate-quotes/new"
                        element={
                            <DashboardLayout>
                                <LclRateQuoteForm />
                            </DashboardLayout>
                        }
                    />


                    {/* ──────────────────────────── AIR FREIGHT ──────────────────────────── */}
                    <Route
                        path="/air/dashboard"
                        element={
                            <DashboardLayout>
                                <AirDashboard />
                            </DashboardLayout>
                        }
                    />
                    {/* Air Export */}
                    <Route
                        path="/air/export/shipments"
                        element={
                            <DashboardLayout>
                                <AirExportList />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/export/shipments/:id"
                        element={
                            <DashboardLayout>
                                <AirShipmentDetail />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/export/rate-quotes"
                        element={
                            <DashboardLayout>
                                <AirRateQuoteList kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/export/rate-quote/print/:id"
                        element={<AirRateQuotePrint kind="export" />}
                    />
                    <Route
                        path="/air/export/rate-quote/new"
                        element={
                            <DashboardLayout>
                                <AirRateQuoteForm kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/export/rate-quote/:id"
                        element={
                            <DashboardLayout>
                                <AirRateQuoteForm kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/export/cancel-quotes"
                        element={
                            <DashboardLayout>
                                <AirCancelRateQuoteList kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/export/cancel-shipments"
                        element={
                            <DashboardLayout>
                                <ExportCancelShipmentList />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/export/online-bookings"
                        element={
                            <DashboardLayout>
                                <CargoAiOnlineBookings />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/export/booking"
                        element={
                            <DashboardLayout>
                                <BookingPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/export/booking/:id"
                        element={
                            <DashboardLayout>
                                <BookingPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/export/booking/:id/hawb"
                        element={
                            <DashboardLayout>
                                <HawbPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/export/booking/:id/mawb"
                        element={
                            <DashboardLayout>
                                <MawbPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/booking"
                        element={
                            <DashboardLayout>
                                <BookingPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/booking/:id"
                        element={
                            <DashboardLayout>
                                <BookingPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/booking/:id/hawb"
                        element={
                            <DashboardLayout>
                                <HawbPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/booking/:id/mawb"
                        element={
                            <DashboardLayout>
                                <MawbPage />
                            </DashboardLayout>
                        }
                    />
                    {/* Air Import */}
                    <Route
                        path="/air/import/shipments"
                        element={
                            <DashboardLayout>
                                <AirImportList />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/shipments/:id"
                        element={
                            <DashboardLayout>
                                <AirShipmentDetail />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/rate-quotes"
                        element={
                            <DashboardLayout>
                                <AirRateQuoteList kind="import" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/rate-quote/print/:id"
                        element={<AirRateQuotePrint kind="import" />}
                    />
                    <Route
                        path="/air/import/rate-quote/new"
                        element={
                            <DashboardLayout>
                                <AirRateQuoteForm kind="import" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/rate-quote/:id"
                        element={
                            <DashboardLayout>
                                <AirRateQuoteForm kind="import" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/cancel-quotes"
                        element={
                            <DashboardLayout>
                                <AirCancelRateQuoteList kind="import" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/cancel-shipments"
                        element={
                            <DashboardLayout>
                                <ImportCancelShipmentList />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/pre-alert"
                        element={
                            <DashboardLayout>
                                <AirImportPreAlert />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/arrival-notice"
                        element={
                            <DashboardLayout>
                                <AirImportArrivalNotice />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/customs"
                        element={
                            <DashboardLayout>
                                <AirImportCustoms />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/delivery-order"
                        element={
                            <DashboardLayout>
                                <AirImportDeliveryOrder />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/cargo-delivery"
                        element={
                            <DashboardLayout>
                                <AirImportCargoDelivery />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/pod"
                        element={
                            <DashboardLayout>
                                <AirImportPOD />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/:kind/status-audit/:id"
                        element={
                            <DashboardLayout>
                                <AirStatusAudit />
                            </DashboardLayout>
                        }
                    />
                    {/* Ocean Schedule (INTTRA) & Tracking */}
                    <Route
                        path="/ocean/schedule"
                        element={
                            <DashboardLayout>
                                <OceanSchedule />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/tracking"
                        element={
                            <DashboardLayout>
                                <OceanContainerTracking />
                            </DashboardLayout>
                        }
                    />
                    {/* Domestic Freight (Export & Import: Rate Quotes + Work Orders) */}
                    <Route
                        path="/domestic/export/rate-quotes"
                        element={
                            <DashboardLayout>
                                <DomesticRateQuoteList kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/export/rate-quote/new"
                        element={
                            <DashboardLayout>
                                <DomesticRateQuoteForm kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/export/rate-quote/:id"
                        element={
                            <DashboardLayout>
                                <DomesticRateQuoteForm kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/export/work-orders"
                        element={
                            <DashboardLayout>
                                <DomesticWorkOrderList kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/export/work-order/new"
                        element={
                            <DashboardLayout>
                                <DomesticWorkOrderForm kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/export/work-order/:id"
                        element={
                            <DashboardLayout>
                                <DomesticWorkOrderForm kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/import/rate-quotes"
                        element={
                            <DashboardLayout>
                                <DomesticRateQuoteList kind="import" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/import/rate-quote/new"
                        element={
                            <DashboardLayout>
                                <DomesticRateQuoteForm kind="import" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/import/rate-quote/:id"
                        element={
                            <DashboardLayout>
                                <DomesticRateQuoteForm kind="import" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/import/work-orders"
                        element={
                            <DashboardLayout>
                                <DomesticWorkOrderList kind="import" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/import/work-order/new"
                        element={
                            <DashboardLayout>
                                <DomesticWorkOrderForm kind="import" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/import/work-order/:id"
                        element={
                            <DashboardLayout>
                                <DomesticWorkOrderForm kind="import" />
                            </DashboardLayout>
                        }
                    />
                    {/* Domestic Reports */}
                    <Route
                        path="/domestic/export/reports"
                        element={
                            <DashboardLayout>
                                <DomesticReportsHome kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/export/reports/:reportId"
                        element={
                            <DashboardLayout>
                                <DomesticReportPage kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/import/reports"
                        element={
                            <DashboardLayout>
                                <DomesticReportsHome kind="import" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/import/reports/:reportId"
                        element={
                            <DashboardLayout>
                                <DomesticReportPage kind="import" />
                            </DashboardLayout>
                        }
                    />
                    {/* Air Freight Reports */}
                    <Route
                        path="/air/export/reports"
                        element={
                            <DashboardLayout>
                                <AirReportsHome kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/export/reports/top-50-customer"
                        element={
                            <DashboardLayout>
                                <AirExportTopFiftyCustomerReport />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/export/reports/:reportId"
                        element={
                            <DashboardLayout>
                                <AirReportPage kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/reports"
                        element={
                            <DashboardLayout>
                                <AirReportsHome kind="import" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/reports/:reportId"
                        element={
                            <DashboardLayout>
                                <AirReportPage kind="import" />
                            </DashboardLayout>
                        }
                    />
                    {/* Ocean preview reports (mock data — not live FCL API reports) */}
                    <Route
                        path="/ocean/export/reports/:reportId"
                        element={
                            <DashboardLayout>
                                <OceanReportPage kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/:reportId"
                        element={
                            <DashboardLayout>
                                <OceanReportPage kind="import" />
                            </DashboardLayout>
                        }
                    />
                    {/* Tracking (accessible via shipment detail) */}
                    <Route
                        path="/air/tracking"
                        element={
                            <DashboardLayout>
                                <AirTracking />
                            </DashboardLayout>
                        }
                    />
                    {/* ──────────────────────────── ADMIN PANEL ──────────────────────────── */}
                    <Route
                        path="/user_permission"
                        element={
                            <DashboardLayout>
                                <UserPermissionPage />
                            </DashboardLayout>
                        }
                    />
                    {/* <Route


                    <Route
                        path="/ocean/export/reports/rate-quote"
                        element={
                            <DashboardLayout>
                                <FclExportRateQuoteReport />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/export/reports/onboard-status"
                        element={
                            <DashboardLayout>
                                <FclExportOnboardStatusReport />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/export/reports/advanced-status"
                        element={
                            <DashboardLayout>
                                <FclExportAdvancedStatusReport />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports"
                        element={
                            <DashboardLayout>
                                <FclReportsHome kind="import" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/booking"
                        element={
                            <DashboardLayout>
                                <FclImportBookingReportPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/active-shipment"
                        element={
                            <DashboardLayout>
                                <FclImportActiveShipmentReportPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/demurrage"
                        element={
                            <DashboardLayout>
                                <FclImportContainerDemurrageReportPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/trucking"
                        element={
                            <DashboardLayout>
                                <FclImportTruckingReportPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/status-audit"
                        element={
                            <DashboardLayout>
                                <FclImportStatusAuditReportPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/container-tracking"
                        element={
                            <DashboardLayout>
                                <FclImportContainerTrackingReportPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/arrival-notice"
                        element={
                            <DashboardLayout>
                                <FclImportArrivalNoticeReportPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/eta-status"
                        element={
                            <DashboardLayout>
                                <FclImportEtaStatusReportPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/file-cancellation-report"
                        element={
                            <DashboardLayout>
                                <FclImportFileCancellationReportPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/export/reports/file-cancellation"
                        element={
                            <DashboardLayout>
                                <FclImportFileCancellationReportPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/arrival-notice-pre-alert-report"
                        element={
                            <DashboardLayout>
                                <FclImportArrivalNoticePreAlertReportPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/profit-loss-report"
                        element={
                            <DashboardLayout>
                                <FclImportProfitLossReportPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/rate-quote-report"
                        element={
                            <DashboardLayout>
                                <FclImportRateQuoteReportPage />
                            </DashboardLayout>
                        }
                    />
                    {/* OCEAN submenu routes */}
                    <Route
                        path="/rate-quote"
                        element={
                            <DashboardLayout>
                                <RateQuote />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/cancel-rate-quote"
                        element={
                            <DashboardLayout>
                                <CancelRateQuote />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/rate-quote/new"
                        element={
                            <DashboardLayout>
                                <NewRateQuote />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/rate-quote/edit/:id"
                        element={
                            <DashboardLayout>
                                <NewRateQuote />
                            </DashboardLayout>
                        }
                    />
                    <Route path="/rate-quote/print/:id" element={<RateQuotePrint />} />
                    {/* HBL & MBL routes */}
                    <Route
                        path="/hbl"
                        element={
                            <DashboardLayout>
                                <HBL />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/hblPrint"
                        element={
                            <DashboardLayout>
                                <HBLPrint />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/certificateOfOriginPrint"
                        element={
                            <DashboardLayout>
                                <CertificateOfOriginPrint />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/customerInvoicePrint"
                        element={
                            <DashboardLayout>
                                <CustomerInvoicePrint />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/preAlertPrint"
                        element={
                            <DashboardLayout>
                                <PreAlertPrint />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/workOrderPrint"
                        element={
                            <DashboardLayout>
                                <WorkOrderPrint />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/packingListPrint"
                        element={
                            <DashboardLayout>
                                <PackingListPrint />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/fcrPrint"
                        element={
                            <DashboardLayout>
                                <FCRPrint />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/customsInstructionsPrint"
                        element={
                            <DashboardLayout>
                                <CustomsInstructionsPrint />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/transportOrderPrint"
                        element={
                            <DashboardLayout>
                                <TransportOrderPrint />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/coverPagePrint"
                        element={
                            <DashboardLayout>
                                <CoverPagePrint />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/securityEndorsementPrint"
                        element={<SecurityEndorsementPrint />}
                    />
                    <Route
                        path="/screeningConsentPrint"
                        element={<ScreeningConsentPrint />}
                    />
                    <Route
                        path="/mbl"
                        element={
                            <DashboardLayout>
                                <MBL />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/mblPrint"
                        element={
                            <DashboardLayout>
                                <MBLPrint />
                            </DashboardLayout>
                        }
                    />
                    {/* Status Audit route */}
                    <Route
                        path="/status-audit"
                        element={
                            <DashboardLayout>
                                <StatusAudit />
                            </DashboardLayout>
                        }
                    />
                    {/* Shipment EDI Events */}
                    <Route
                        path="/shipment-events"
                        element={
                            <DashboardLayout>
                                <ShipmentEventsList />
                            </DashboardLayout>
                        }
                    />
                    {/* RFQ route */}
                    <Route
                        path="/rfq"
                        element={
                            <DashboardLayout>
                                <RFQ />
                            </DashboardLayout>
                        }
                    />
                    {/* AI Agent routes */}
                    <Route
                        path="/ai-agent-setup"
                        element={
                            <DashboardLayout>
                                <AIAgentSetup />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ai-agent-dashboard"
                        element={
                            <DashboardLayout>
                                <AIAgentDashboard />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/agent-control-tower"
                        element={
                            <DashboardLayout>
                                <AgentControlTower />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ai-agent-reports"
                        element={
                            <DashboardLayout>
                                <AIAgentReports />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/compliance-agent"
                        element={
                            <DashboardLayout>
                                <ComplianceAgent />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/maintain-vessel"
                        element={
                            <DashboardLayout>
                                <MaintainVessels />
                            </DashboardLayout>
                        }
                    />
                    {/* Booking */}
                    <Route
                        path="/Commodity"
                        element={
                            <DashboardLayout>
                                <Commodity />
                            </DashboardLayout>
                        }
                    />
                    {/* Mail Compose */}
                    <Route
                        path="/mail/compose"
                        element={
                            <DashboardLayout>
                                <ComposeMail />
                            </DashboardLayout>
                        }
                    />
                    {/* System Maintenance */}
                    <Route
                        path="/CountryClause"
                        element={
                            <DashboardLayout>
                                <CountryClause />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/Clause"
                        element={
                            <DashboardLayout>
                                <MaintainClause />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ChargesCode"
                        element={
                            <DashboardLayout>
                                <ChargesCode />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/status-audit/print"
                        element={<PrintAuditStatusPgae />}
                    />
                    <Route
                        path="/status-audit/print/:id"
                        element={<PrintAuditStatusPgae />}
                    />
                    <Route
                        path="/port-place"
                        element={
                            <DashboardLayout>
                                <PortPlace />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/Users"
                        element={
                            <DashboardLayout>
                                <MaintainUsers />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/customers"
                        element={
                            <DashboardLayout>
                                <CustomerPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/login-history"
                        element={
                            <DashboardLayout>
                                <LoginHistory />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/applicable-charges"
                        element={
                            <DashboardLayout>
                                <ApplicableCharges />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/office-type"
                        element={
                            <DashboardLayout>
                                <OfficeTypes />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/terminal-mapping"
                        element={
                            <DashboardLayout>
                                <TerminalMapping />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/terminal-mapping/new"
                        element={
                            <DashboardLayout>
                                <TerminalMappingForm />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/terminal-mapping/edit/:id"
                        element={
                            <DashboardLayout>
                                <TerminalMappingForm />
                            </DashboardLayout>
                        }
                    />
                    {/* Notification Center */}
                    <Route
                        path="/notifications"
                        element={
                            <DashboardLayout>
                                <NotificationCenterPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/dock-receipt/:bookingId"
                        element={
                            <DashboardLayout>
                                <DockReceipt />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/dock-receipt/print/:bookingId"
                        element={<DockReceiptPrint />}
                    />
                    {/* Job Costing */}
                    <Route
                        path="/job-costing/:bookingId"
                        element={
                            <DashboardLayout>
                                <JobCosting />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-job-costing/:bookingId"
                        element={
                            <DashboardLayout>
                                <JobCosting />
                            </DashboardLayout>
                        }
                    />
                    {/* Accounting Module */}
                    <Route
                        path="/accounting"
                        element={
                            <DashboardLayout>
                                <ChartOfAccounts />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/accounting/chart-of-accounts"
                        element={
                            <DashboardLayout>
                                <ChartOfAccounts />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/accounting/charge-codes"
                        element={
                            <DashboardLayout>
                                <AccountingChargeCodes />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/accounting/invoices"
                        element={
                            <DashboardLayout>
                                <CustomerInvoices />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/accounting/bills"
                        element={
                            <DashboardLayout>
                                <VendorBills />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/accounting/journals"
                        element={
                            <DashboardLayout>
                                <JournalEntries />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/accounting/payments"
                        element={
                            <DashboardLayout>
                                <Payments />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/accounting/reports"
                        element={
                            <DashboardLayout>
                                <AccountingReports />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/maintain-locations"
                        element={
                            <DashboardLayout>
                                <MaintainLocations />
                            </DashboardLayout>
                        }
                    />
                    {/* Ocean Import Module */}
                    <Route
                        path="/import-rate-quote"
                        element={
                            <DashboardLayout>
                                <ImportRateQuoteList />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-cancel-rate-quote"
                        element={
                            <DashboardLayout>
                                <ImportCancelRateQuoteList />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-rate-quote/new"
                        element={
                            <DashboardLayout>
                                <ImportRateQuoteForm />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-rate-quote/edit/:id"
                        element={
                            <DashboardLayout>
                                <ImportRateQuoteForm />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-rate-quote/print/:id"
                        element={<ImportRateQuotePrint />}
                    />
                    <Route
                        path="/import-booking"
                        element={
                            <DashboardLayout>
                                <ImportBookingList />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-booking-fcl"
                        element={
                            <DashboardLayout>
                                <ImportBookingList shippingTerm="FCL" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-booking-lcl"
                        element={
                            <DashboardLayout>
                                <ImportBookingList shippingTerm="LCL" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-cancel-booking"
                        element={
                            <DashboardLayout>
                                <ImportCancelBookingRequest />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-booking/cancel"
                        element={
                            <DashboardLayout>
                                <CancelImportShipment />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-booking/new"
                        element={
                            <DashboardLayout>
                                <ImportBookingForm />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-booking/edit/:id"
                        element={
                            <DashboardLayout>
                                <ImportBookingForm />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-booking/Print/:id"
                        element={<BookingImportPrint />}
                    />
                    <Route
                        path="/import-booking/PrintLockerFreight/:id"
                        element={
                            <DashboardLayout>
                                <LockerFreightImportBookingPrint />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-booking/PrintNonFreight/:id"
                        element={<BookingImportPrintNonFreight />}
                    />
                    <Route
                        path="/release-order"
                        element={
                            <DashboardLayout>
                                <ReleaseOrderList />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/release-order/new"
                        element={
                            <DashboardLayout>
                                <ReleaseOrderForm />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/release-order/edit"
                        element={
                            <DashboardLayout>
                                <ReleaseOrderForm />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/release-order/print/:id"
                        element={<ReleaseOrderPrint />}
                    />
                    <Route
                        path="/delivery-order/new"
                        element={
                            <DashboardLayout>
                                <DeliveryOrderForm />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/delivery-order/edit/:id"
                        element={
                            <DashboardLayout>
                                <DeliveryOrderForm />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/delivery-order/print/:id"
                        element={<DeliveryOrderPrint />}
                    />
                    <Route
                        path="/import-booking/status-audit/:id"
                        element={
                            <DashboardLayout>
                                <ImportStatusAudit />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-status-audit/print/:companyId/:bookingId"
                        element={<ImportPrintAuditStatusPgae />}
                    />
                    <Route
                        path="/import-hbl-print/:bookingId"
                        element={
                            <DashboardLayout>
                                <ImportHBLPrint />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/import-pre-alert-consignee-print/:bookingId"
                        element={<ImportPreAlertConsigneePrint />}
                    />
                    <Route
                        path="/import-booking-confirmation-print/:bookingId"
                        element={<ImportBookingConfirmationPrint />}
                    />
                    <Route
                        path="/import-isf-print/:bookingId"
                        element={
                            <DashboardLayout>
                                <ISFPrint />
                            </DashboardLayout>
                        }
                    />
                    {/* ──────────────────────────── AIR FREIGHT ──────────────────────────── */}
                    <Route
                        path="/air/dashboard"
                        element={
                            <DashboardLayout>
                                <AirDashboard />
                            </DashboardLayout>
                        }
                    />
                    {/* Air Export */}
                    <Route
                        path="/air/export/shipments"
                        element={
                            <DashboardLayout>
                                <AirExportList />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/export/shipments/:id"
                        element={
                            <DashboardLayout>
                                <AirShipmentDetail />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/export/rate-quotes"
                        element={
                            <DashboardLayout>
                                <AirRateQuoteList kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/export/rate-quote/print/:id"
                        element={<AirRateQuotePrint kind="export" />}
                    />
                    <Route
                        path="/air/export/rate-quote/new"
                        element={
                            <DashboardLayout>
                                <AirRateQuoteForm kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/export/rate-quote/:id"
                        element={
                            <DashboardLayout>
                                <AirRateQuoteForm kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/export/cancel-quotes"
                        element={
                            <DashboardLayout>
                                <AirCancelRateQuoteList kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/export/cancel-shipments"
                        element={
                            <DashboardLayout>
                                <ExportCancelShipmentList />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/export/online-bookings"
                        element={
                            <DashboardLayout>
                                <CargoAiOnlineBookings />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/export/booking"
                        element={
                            <DashboardLayout>
                                <BookingPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/export/booking/:id"
                        element={
                            <DashboardLayout>
                                <BookingPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/export/booking/:id/hawb"
                        element={
                            <DashboardLayout>
                                <HawbPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/export/booking/:id/mawb"
                        element={
                            <DashboardLayout>
                                <MawbPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/booking"
                        element={
                            <DashboardLayout>
                                <BookingPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/booking/:id"
                        element={
                            <DashboardLayout>
                                <BookingPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/booking/:id/hawb"
                        element={
                            <DashboardLayout>
                                <HawbPage />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/booking/:id/mawb"
                        element={
                            <DashboardLayout>
                                <MawbPage />
                            </DashboardLayout>
                        }
                    />
                    {/* Air Import */}
                    <Route
                        path="/air/import/shipments"
                        element={
                            <DashboardLayout>
                                <AirImportList />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/shipments/:id"
                        element={
                            <DashboardLayout>
                                <AirShipmentDetail />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/rate-quotes"
                        element={
                            <DashboardLayout>
                                <AirRateQuoteList kind="import" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/rate-quote/print/:id"
                        element={<AirRateQuotePrint kind="import" />}
                    />
                    <Route
                        path="/air/import/rate-quote/new"
                        element={
                            <DashboardLayout>
                                <AirRateQuoteForm kind="import" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/rate-quote/:id"
                        element={
                            <DashboardLayout>
                                <AirRateQuoteForm kind="import" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/cancel-quotes"
                        element={
                            <DashboardLayout>
                                <AirCancelRateQuoteList kind="import" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/cancel-shipments"
                        element={
                            <DashboardLayout>
                                <ImportCancelShipmentList />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/pre-alert"
                        element={
                            <DashboardLayout>
                                <AirImportPreAlert />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/arrival-notice"
                        element={
                            <DashboardLayout>
                                <AirImportArrivalNotice />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/customs"
                        element={
                            <DashboardLayout>
                                <AirImportCustoms />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/delivery-order"
                        element={
                            <DashboardLayout>
                                <AirImportDeliveryOrder />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/cargo-delivery"
                        element={
                            <DashboardLayout>
                                <AirImportCargoDelivery />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/pod"
                        element={
                            <DashboardLayout>
                                <AirImportPOD />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/:kind/status-audit/:id"
                        element={
                            <DashboardLayout>
                                <AirStatusAudit />
                            </DashboardLayout>
                        }
                    />
                    {/* Ocean Schedule (INTTRA) & Tracking */}
                    <Route
                        path="/ocean/schedule"
                        element={
                            <DashboardLayout>
                                <OceanSchedule />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/tracking"
                        element={
                            <DashboardLayout>
                                <OceanContainerTracking />
                            </DashboardLayout>
                        }
                    />
                    {/* Domestic Freight (Export & Import: Rate Quotes + Work Orders) */}
                    <Route
                        path="/domestic/export/rate-quotes"
                        element={
                            <DashboardLayout>
                                <DomesticRateQuoteList kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/export/rate-quote/new"
                        element={
                            <DashboardLayout>
                                <DomesticRateQuoteForm kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/export/rate-quote/:id"
                        element={
                            <DashboardLayout>
                                <DomesticRateQuoteForm kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/export/work-orders"
                        element={
                            <DashboardLayout>
                                <DomesticWorkOrderList kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/export/work-order/new"
                        element={
                            <DashboardLayout>
                                <DomesticWorkOrderForm kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/export/work-order/:id"
                        element={
                            <DashboardLayout>
                                <DomesticWorkOrderForm kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/import/rate-quotes"
                        element={
                            <DashboardLayout>
                                <DomesticRateQuoteList kind="import" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/import/rate-quote/new"
                        element={
                            <DashboardLayout>
                                <DomesticRateQuoteForm kind="import" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/import/rate-quote/:id"
                        element={
                            <DashboardLayout>
                                <DomesticRateQuoteForm kind="import" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/import/work-orders"
                        element={
                            <DashboardLayout>
                                <DomesticWorkOrderList kind="import" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/import/work-order/new"
                        element={
                            <DashboardLayout>
                                <DomesticWorkOrderForm kind="import" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/import/work-order/:id"
                        element={
                            <DashboardLayout>
                                <DomesticWorkOrderForm kind="import" />
                            </DashboardLayout>
                        }
                    />
                    {/* Domestic Reports */}
                    <Route
                        path="/domestic/export/reports"
                        element={
                            <DashboardLayout>
                                <DomesticReportsHome kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/export/reports/:reportId"
                        element={
                            <DashboardLayout>
                                <DomesticReportPage kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/import/reports"
                        element={
                            <DashboardLayout>
                                <DomesticReportsHome kind="import" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/domestic/import/reports/:reportId"
                        element={
                            <DashboardLayout>
                                <DomesticReportPage kind="import" />
                            </DashboardLayout>
                        }
                    />
                    {/* Air Freight Reports */}
                    <Route
                        path="/air/export/reports"
                        element={
                            <DashboardLayout>
                                <AirReportsHome kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/export/reports/top-50-customer"
                        element={
                            <DashboardLayout>
                                <AirExportTopFiftyCustomerReport />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/export/reports/:reportId"
                        element={
                            <DashboardLayout>
                                <AirReportPage kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/reports"
                        element={
                            <DashboardLayout>
                                <AirReportsHome kind="import" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/air/import/reports/:reportId"
                        element={
                            <DashboardLayout>
                                <AirReportPage kind="import" />
                            </DashboardLayout>
                        }
                    />
                    {/* Ocean preview reports (mock data — not live FCL API reports) */}
                    <Route
                        path="/ocean/export/reports/:reportId"
                        element={
                            <DashboardLayout>
                                <OceanReportPage kind="export" />
                            </DashboardLayout>
                        }
                    />
                    <Route
                        path="/ocean/import/reports/:reportId"
                        element={
                            <DashboardLayout>
                                <OceanReportPage kind="import" />
                            </DashboardLayout>
                        }
                    />
                    {/* Tracking (accessible via shipment detail) */}
                    <Route
                        path="/air/tracking"
                        element={
                            <DashboardLayout>
                                <AirTracking />
                            </DashboardLayout>
                        }
                    />
                    {/* ──────────────────────────── ADMIN PANEL ──────────────────────────── */}
                    <Route
                        path="/user_permission"
                        element={
                            <DashboardLayout>
                                <UserPermissionPage />
                            </DashboardLayout>
                        }
                    />
                    {/* <Route
            path="/admin/*"
            element={
              <DashboardLayout>
                <AdminRoutes />
              </DashboardLayout>
            }
          /> */}
                    {/* <Route path="/admin/*" element={<DashboardLayout><AdminPanel /></DashboardLayout>} /> */}
                </>
            )}
            {/* //   ) : ( */}
      //{" "}
            <>
        // {/* default login page */}
        //{" "}
                {/* <Route path="/login" element={<Login />} />
    //       <Route path="/" element={<Navigate to="/login" replace />} /> */}
        // {/* for the infox company login page */}
        //{" "}
                {/* <Route path="/infox/login" element={<Login2 />} />
    //                 <Route path="/" element={<Navigate to="/infox/login" replace />} /> */}
        // {/* this page used for lockerd freight login page */}
        //{" "}
                {/* <Route path="/locker-freight/login" element={<Login2 />} />
    //                 <Route path="/" element={<Navigate to="/locker-freight/login" replace />} /> */}
        //{" "}
            </>
            {/* //   )} */}
            {/* Locker Freight Login demo logn path*/}
            {/* <Route path="/locker-freight/login" element={<Login2 />} /> */}
            {/* <Route path="/infox/login" element={<Login2 />} /> */}
            {/* <Route path="/login" element={<Login />} /> */}
            {/* <Route path="/admin/login" element={<Login />} /> */}
            {/* Admin Panel */}
            <Route path="/adminLogin" element={<AdminLogin />} />
            {/* Public Routes (accessible without authentication) */}
            <Route path="/quote-response" element={<QuoteResponse />} />
            {/* <Route path="/signup" element={<SignupWizard />} />
      <Route path="/signup/success" element={<SignupSuccess />} /> */}
            {/* <Route
                path="*"
                element={
                    isAuthenticated ? (
                        authUser?.companyId ? (
                            <Navigate to={getPostLoginPath(authUser)} replace />
                        ) : (
                            <Navigate to="/" replace />
                        )
                    ) : (
                        <Navigate to="/" replace />
                    )
                }
            /> */}
            {/* <Route path="*" element={isAuthenticated ? <Navigate to="/modules" /> : <Navigate to="/" />} /> */}
            {/*  this page used for lockerd freight login page */}
            <Route path="*" element={isAuthenticated ? <Navigate to="/import-booking-fcl" /> : <Navigate to="/" />} />
        </Routes>
    );
};

export default MainRoutes;
